if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      /* Shuddh Swad (WP-PDP v3, perf): on phones ss-pdp.css keeps the images of slides 2+ unrendered while the
         wrapper has .ss-gal-defer, so Chrome's lazy loader does not fetch them inside the main image's LCP window
         (cold ad clicks). They are released once the first image has loaded and the page is idle, or at once on
         the first touch, scroll or focus in the gallery, a thumbnail click or a variant change. */
      connectedCallback() {
        this.ssWrap = this.closest('.ss-gal-defer');
        if (!this.ssWrap || this.ssReleaseSlides) return;
        const list = this.querySelector('.product__media-list');
        const opts = { passive: true };
        const release = () => {
          if (!this.ssWrap) return;
          this.ssWrap.classList.remove('ss-gal-defer');
          this.ssWrap = null;
          ['pointerdown', 'touchstart', 'focusin'].forEach((type) => this.removeEventListener(type, release, opts));
          if (list) list.removeEventListener('scroll', release, opts);
          window.clearTimeout(this.ssReleaseTimer);
        };
        this.ssReleaseSlides = release;
        ['pointerdown', 'touchstart', 'focusin'].forEach((type) => this.addEventListener(type, release, opts));
        if (list) list.addEventListener('scroll', release, opts);
        const idle = () =>
          'requestIdleCallback' in window ? window.requestIdleCallback(release, { timeout: 1500 }) : window.setTimeout(release, 300);
        const first = this.querySelector('.product__media-item img');
        if (!first || first.complete) {
          idle();
        } else {
          first.addEventListener('load', idle, { once: true });
          first.addEventListener('error', idle, { once: true });
        }
        /* Safety net: never leave the slides empty if the first image stalls. */
        this.ssReleaseTimer = window.setTimeout(release, 10000);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        if (this.ssReleaseSlides) this.ssReleaseSlides();
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        // Shuddh Swad (WP-PDP v3): on phones a variant change (prepend) swaps the first slide in place; the page
        // must not jump up to the gallery, away from the pack cards and Add to cart. Thumbnail clicks and
        // desktop keep Dawn's behaviour.
        const keepPage = prepend && !this.mql.matches;
        if (!keepPage) this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (keepPage) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
